import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Mic,
  FileText,
  Brain,
  Shield,
  Stethoscope,
  Activity,
  Eye,
  Image,
  MessageSquare,
  Zap,
  CheckCircle,
  Users,
  Building2,
  Laptop,
  Smartphone,
  Lock,
  Server,
  RefreshCw
} from 'lucide-react';
import { LandingLayout } from '../components/LandingLayout';
import {
  AnimatedSection,
  StaggerContainer,
  StaggerItem,
  ScaleIn
} from '../components/AnimatedSection';
import { useLanguage } from '../i18n';

const integrations = [
  { name: 'Epic', logo: '🏥' },
  { name: 'Cerner', logo: '💊' },
  { name: 'Meditech', logo: '⚕️' },
  { name: 'Allscripts', logo: '📋' },
  { name: 'athenahealth', logo: '🏛️' },
  { name: 'NextGen', logo: '🔬' },
];

export const SolutionsPage = () => {
  const { t, dir } = useLanguage();

  const mainFeatures = [
    {
      icon: Mic,
      title: t.solutions.voiceDoc.title,
      description: t.solutions.voiceDoc.description,
      benefits: t.solutions.voiceDoc.benefits,
      color: 'from-forest to-forest-400'
    },
    {
      icon: Brain,
      title: t.solutions.aiAssistant.title,
      description: t.solutions.aiAssistant.description,
      benefits: t.solutions.aiAssistant.benefits,
      color: 'from-bark-700 to-bark-500'
    },
    {
      icon: FileText,
      title: t.solutions.soapNotes.title,
      description: t.solutions.soapNotes.description,
      benefits: t.solutions.soapNotes.benefits,
      color: 'from-forest-400 to-accent'
    },
    {
      icon: Image,
      title: t.solutions.imageAnalysis.title,
      description: t.solutions.imageAnalysis.description,
      benefits: t.solutions.imageAnalysis.benefits,
      color: 'from-bark-500 to-bark-300'
    },
    {
      icon: MessageSquare,
      title: t.solutions.patientComm.title,
      description: t.solutions.patientComm.description,
      benefits: t.solutions.patientComm.benefits,
      color: 'from-forest to-accent'
    },
    {
      icon: Activity,
      title: t.solutions.analyticsDash.title,
      description: t.solutions.analyticsDash.description,
      benefits: t.solutions.analyticsDash.benefits,
      color: 'from-bark-700 to-forest'
    },
  ];

  const specialties = [
    { name: t.solutions.generalPractice, icon: Stethoscope },
    { name: t.solutions.internalMedicine, icon: Activity },
    { name: t.solutions.pediatrics, icon: Users },
    { name: t.solutions.cardiology, icon: Activity },
    { name: t.solutions.dermatology, icon: Eye },
    { name: t.solutions.orthopedics, icon: Users },
    { name: t.solutions.psychiatry, icon: Brain },
    { name: t.solutions.radiology, icon: Image },
  ];

  const securityFeatures = [
    {
      icon: Lock,
      title: t.solutions.encryption,
      description: t.solutions.encryptionDesc
    },
    {
      icon: Shield,
      title: t.solutions.hipaaGdpr,
      description: t.solutions.hipaaGdprDesc
    },
    {
      icon: Server,
      title: t.solutions.soc2,
      description: t.solutions.soc2Desc
    },
    {
      icon: RefreshCw,
      title: t.solutions.audits,
      description: t.solutions.auditsDesc
    },
  ];

  return (
    <LandingLayout>
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden" dir={dir}>
        <div className="absolute inset-0 bg-gradient-to-br from-forest/5 via-accent/5 to-transparent" />
        <div className="absolute top-40 -end-40 w-[500px] h-[500px] bg-forest/10 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection className="text-center max-w-4xl mx-auto">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-forest/10 text-forest text-sm font-medium mb-6">
              <Zap className="w-4 h-4" />
              {t.solutions.badge}
            </span>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-bark-700 leading-tight mb-6">
              {t.solutions.title}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-forest to-forest-400">
                {t.solutions.titleHighlight}
              </span>
            </h1>

            <p className="text-xl text-bark-700/60 mb-10 max-w-2xl mx-auto">
              {t.solutions.description}
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-bark-700 text-white font-semibold hover:bg-bark-600 transition-all shadow-lg shadow-bark-700/25 hover:shadow-bark-700/40 hover:-translate-y-0.5"
              >
                {t.solutions.tryFree}
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-sand-300/30 text-bark-700 font-semibold hover:bg-sand-300/50 transition-all"
              >
                {t.solutions.viewPricing}
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Main Features Section */}
      <section className="py-20 lg:py-28 bg-sand-300/30" id="features" dir={dir}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-bark-700 mb-4">
              {t.solutions.featuresTitle}
            </h2>
            <p className="text-xl text-bark-700/60 max-w-2xl mx-auto">
              {t.solutions.featuresDesc}
            </p>
          </AnimatedSection>

          <div className="space-y-20">
            {mainFeatures.map((feature, index) => (
              <AnimatedSection
                key={index}
                direction={index % 2 === 0 ? 'left' : 'right'}
              >
                <div className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                  <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-6`}>
                      <feature.icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl lg:text-3xl font-bold text-bark-700 mb-4">
                      {feature.title}
                    </h3>
                    <p className="text-lg text-bark-700/60 mb-6">
                      {feature.description}
                    </p>
                    <ul className="space-y-3">
                      {feature.benefits.map((benefit, i) => (
                        <li key={i} className="flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-forest flex-shrink-0" />
                          <span className="text-bark-700">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className={`relative ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                    <div className={`bg-gradient-to-br ${feature.color} rounded-3xl p-8 lg:p-12`}>
                      <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 lg:p-8">
                        <div className="space-y-4">
                          <div className="h-4 bg-white/30 rounded w-3/4" />
                          <div className="h-4 bg-white/20 rounded w-1/2" />
                          <div className="h-4 bg-white/30 rounded w-2/3" />
                          <div className="h-20 bg-white/20 rounded-lg mt-6" />
                          <div className="flex gap-3 mt-4">
                            <div className="h-10 bg-white/40 rounded-lg flex-1" />
                            <div className="h-10 bg-white/20 rounded-lg flex-1" />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="absolute -bottom-4 -end-4 w-24 h-24 bg-forest/20 rounded-full blur-2xl" />
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Specialties Section */}
      <section className="py-20 lg:py-28" dir={dir}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-bark-700 mb-4">
              {t.solutions.specialtiesTitle}
            </h2>
            <p className="text-xl text-bark-700/60 max-w-2xl mx-auto">
              {t.solutions.specialtiesDesc}
            </p>
          </AnimatedSection>

          <StaggerContainer className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {specialties.map((specialty, index) => (
              <StaggerItem key={index}>
                <div className="group bg-sand-50 rounded-2xl p-6 border border-sand-300 hover:border-forest/30 hover:shadow-lg transition-all text-center">
                  <div className="w-14 h-14 rounded-xl bg-forest/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-forest/20 transition-colors">
                    <specialty.icon className="w-7 h-7 text-forest" />
                  </div>
                  <h3 className="font-semibold text-bark-700">{specialty.name}</h3>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Integrations Section */}
      <section className="py-20 lg:py-28 bg-sand-300/30" dir={dir}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-bark-700 mb-4">
              {t.solutions.integrationsTitle}
            </h2>
            <p className="text-xl text-bark-700/60 max-w-2xl mx-auto">
              {t.solutions.integrationsDesc}
            </p>
          </AnimatedSection>

          <StaggerContainer className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {integrations.map((integration, index) => (
              <StaggerItem key={index}>
                <div className="bg-sand-50 rounded-xl p-6 border border-sand-300 hover:border-forest/30 hover:shadow-md transition-all text-center">
                  <span className="text-4xl mb-3 block">{integration.logo}</span>
                  <p className="font-medium text-bark-700">{integration.name}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>

          <AnimatedSection className="mt-12 text-center" delay={0.3}>
            <p className="text-bark-700/60">
              {t.solutions.customIntegration.split('Contact us')[0]}
              <Link to="/contact" className="text-forest font-medium hover:underline">
                Contact us
              </Link>
              {t.solutions.customIntegration.split('Contact us')[1] || ''}
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Platform Section */}
      <section className="py-20 lg:py-28" dir={dir}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection direction="left">
              <h2 className="text-3xl lg:text-4xl font-bold text-bark-700 mb-6">
                {t.solutions.platformTitle}
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-forest to-forest-400">
                  {t.solutions.platformHighlight}
                </span>
              </h2>
              <p className="text-xl text-bark-700/60 mb-8">
                {t.solutions.platformDesc}
              </p>
              <div className="space-y-4">
                {[
                  { icon: Laptop, text: t.solutions.webApp },
                  { icon: Smartphone, text: t.solutions.mobileApps },
                  { icon: Building2, text: t.solutions.enterpriseDeployment },
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-forest/10 flex items-center justify-center">
                      <item.icon className="w-6 h-6 text-forest" />
                    </div>
                    <span className="text-bark-700">{item.text}</span>
                  </div>
                ))}
              </div>
            </AnimatedSection>

            <AnimatedSection direction="right">
              <div className="relative">
                <div className="bg-gradient-to-br from-bark-700 to-bark-600 rounded-3xl p-8 shadow-2xl">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-bark-500 rounded-xl aspect-video" />
                    <div className="bg-bark-500 rounded-xl aspect-video" />
                    <div className="col-span-2 bg-bark-500 rounded-xl h-32" />
                  </div>
                </div>
                <div className="absolute -bottom-8 -end-8 w-48 h-80 bg-bark-700 rounded-3xl shadow-2xl p-4 hidden lg:block">
                  <div className="bg-bark-500 rounded-xl h-full" />
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="py-20 lg:py-28 bg-bark-700" id="security" dir={dir}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection className="text-center mb-16">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-forest/20 text-forest-300 text-sm font-medium mb-4">
              <Shield className="w-4 h-4" />
              Enterprise Security
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              {t.solutions.securityTitle}
            </h2>
            <p className="text-xl text-sand-400 max-w-2xl mx-auto">
              {t.solutions.securityDesc}
            </p>
          </AnimatedSection>

          <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {securityFeatures.map((feature, index) => (
              <StaggerItem key={index}>
                <div className="bg-bark-600 rounded-2xl p-6 border border-bark-500">
                  <div className="w-12 h-12 rounded-xl bg-forest/20 flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-forest-300" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-sand-400 text-sm">{feature.description}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28" dir={dir}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <ScaleIn>
            <div className="bg-gradient-to-br from-bark-700 to-bark-600 rounded-3xl p-12 lg:p-16 text-center">
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                {t.solutions.ctaTitle}
              </h2>
              <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
                {t.solutions.ctaDesc}
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-accent text-bark-700 font-semibold hover:bg-accent/90 transition-all shadow-lg hover:-translate-y-0.5"
                >
                  {t.cta.startTrial}
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-white/20 text-white font-semibold hover:bg-white/30 transition-all backdrop-blur-sm"
                >
                  {t.contact.scheduleDemo}
                </Link>
              </div>
            </div>
          </ScaleIn>
        </div>
      </section>
    </LandingLayout>
  );
};
